import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAnnouncements, useUniversalAnnouncements } from '../hooks/useAnnouncements';
import { PulseSeverity } from '../types';
import { AnnouncementCard } from '../components/pulse/AnnouncementCard';
import { Button, Spinner, ErrorBanner } from '../components/ui';
import { Plus, Filter, Megaphone, Globe } from 'lucide-react';
import { useCurrentUser } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { AnnouncementComposer } from '../components/pulse/AnnouncementComposer';
import { usePermissions } from '../hooks/usePermissions';
import { EmptyState } from '../components/ui/EmptyState';

type FilterType = 'ALL' | 'CRITICAL' | 'UNREAD' | 'PARENT';

export const PulsePage = () => {
  const { node, isUniversal, isGlobal } = useTenant();
  const { can } = usePermissions();
  
  const nodeAnnouncements = useAnnouncements(node?.id || '', true);
  const universalAnnouncements = useUniversalAnnouncements(isUniversal || isGlobal);
  
  const query = (isUniversal || isGlobal) ? universalAnnouncements : nodeAnnouncements;
  const { data: announcements = [], isLoading, error } = query;
  
  const [activeFilter, setActiveFilter] = useState<FilterType>('ALL');
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const terms = node?.config?.terminology;

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('orgsets_dismissed_announcements');
      return stored ? new Set(JSON.parse(stored)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const handleDismiss = (id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev).add(id);
      try {
        localStorage.setItem('orgsets_dismissed_announcements', JSON.stringify([...next]));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const filteredAnnouncements = announcements
    .filter(ann => !dismissedIds.has(ann.id))
    .filter(ann => {
      if (activeFilter === 'CRITICAL') return ann.severity === PulseSeverity.CRITICAL;
      if (activeFilter === 'UNREAD') return !ann.isAcknowledged && !ann.isRead;
      if (activeFilter === 'PARENT' && !(isUniversal || isGlobal)) return ann.nodeId !== node?.id;
      return true;
    });

  const tabs: { id: FilterType; label: string }[] = [
    { id: 'ALL', label: 'All' },
    { id: 'CRITICAL', label: 'Critical' },
    { id: 'UNREAD', label: 'Unread' },
  ];
  
  if (!isUniversal && !isGlobal) {
    tabs.push({ id: 'PARENT', label: `From ${terms?.parentNode || 'parent nodes'}` });
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 font-medium">Loading announcements...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {error && (
        <ErrorBanner 
          title="Pulse Sync Error" 
          message="We couldn't load the latest announcements. Showing cached data if available."
          onRetry={() => query.refetch()}
        />
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">
              {isUniversal ? "All Activity" : (terms?.pulse || "Pulse")}
            </h1>
            {isUniversal && <Globe size={18} className="text-gray-400" />}
          </div>
          <p className="text-xs md:text-sm text-gray-500">
            {isUniversal 
              ? "A unified view of announcements across all your organizations."
              : "Stay updated with the latest news and announcements."
            }
          </p>
        </div>
        
        {!isUniversal && can('create_announcement') && (
          <div className="flex gap-2">
            <Link 
              to={`/${node?.slug}/pulse`} 
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-none uppercase font-bold tracking-widest font-mono transition-all duration-150 bg-white text-black border-2 border-black hover:bg-[#FFD600] dark:bg-black dark:text-white dark:border-white px-6 py-3 text-xs shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
            >
              <Globe size={18} />
              Public Page
            </Link>
            <Button variant="primary" onClick={() => setIsComposerOpen(true)}>
              <Plus size={18} />
              New announcement
            </Button>
          </div>
        )}
      </div>

      {/* Composer Modal */}
      {node && !isUniversal && (
        <AnnouncementComposer 
          nodeId={node.id} 
          isOpen={isComposerOpen} 
          onClose={() => setIsComposerOpen(false)} 
        />
      )}

      {/* Filters */}
      <div className="flex items-center gap-1 border-b border-black dark:border-white overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveFilter(tab.id)}
            className={cn(
              "px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap",
              activeFilter === tab.id 
                ? "text-blue-700" 
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            {tab.label}
            {activeFilter === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-700" />
            )}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-4">
        {filteredAnnouncements.map((ann) => (
          <AnnouncementCard key={ann.id} announcement={ann} onDismiss={handleDismiss} />
        ))}

        {filteredAnnouncements.length === 0 && (
          <EmptyState
            icon={Megaphone}
            title="No Announcements Found"
            description={
              activeFilter === 'ALL'
                ? 'There are no announcements for this organization yet. Send the first one to keep your members informed.'
                : 'Try changing your filters to see more results.'
            }
            actions={
              activeFilter === 'ALL' && can('create_announcement')
                ? [{ label: 'Send Announcement', onClick: () => setIsComposerOpen(true) }]
                : activeFilter !== 'ALL'
                  ? [{ label: 'Clear Filters', onClick: () => setActiveFilter('ALL'), variant: 'secondary' as const }]
                  : []
            }
            tips={
              activeFilter === 'ALL'
                ? [
                    'Announcements support 4 severity levels: Low, Medium, High, and Critical',
                    'Critical announcements trigger push notifications and optional SMS alerts',
                    'Parent organization announcements cascade down to all child chapters',
                  ]
                : []
            }
          />
        )}
      </div>

      {/* Pagination Footer */}
      {filteredAnnouncements.length > 0 && (
        <div className="flex items-center justify-center pt-4">
          <p className="text-xs text-gray-400">Showing {filteredAnnouncements.length} announcements</p>
        </div>
      )}
    </div>
  );
};
